package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Deliberate handoff survives pending-list replacement; clicking only requests a fresh editor view. */
@Composable
internal fun ProposalsAcceptedDrafts(slug: String, state: ProposalsDecisionState, onReload: () -> Unit) {
    if (state.receipt == null && !state.unavailable && state.accepted.isEmpty()) return
    Column(Modifier.fillMaxWidth().gap(Space.SM).attr("data-proposals-receipt", "true")) {
        if (state.accepted.isNotEmpty()) {
            Column(Modifier.fillMaxWidth().gap(Space.SM).attr("data-proposals-accepted", "true")) {
                LAlert(
                    title = t("app.projects.slug.proposals_accepted_drafts.accepted_drafts", "Accepted drafts"),
                    description = t("app.projects.slug.proposals_accepted_drafts.accepted_as_draft_translations_never_approved",
                        "Accepted as draft translations, never approved. Open each string to inspect its current text and review it."),
                    variant = LAlertVariant.SUCCESS,
                    live = true,
                )
                state.accepted.forEachIndexed { index, draft ->
                    val name = draft.keyName?.takeIf(String::isNotBlank)
                    LLinkButton(
                        label = if (name != null) tf("app.projects.slug.proposals.review_key_in_locale", "Review {key} in {locale}",
                            "key" to name, "locale" to draft.locale)
                        else tf("app.projects.slug.proposals.review_draft_in_locale", "Review draft {number} in {locale}",
                            "number" to index + 1, "locale" to draft.locale),
                        path = "/app/projects/${encodeURIComponent(slug)}/translate/${encodeURIComponent(draft.locale)}?key=${encodeURIComponent(draft.keyId)}",
                        variant = if (index == 0) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
                    )
                }
            }
        }
        state.receipt?.let { receipt ->
            if (receipt.decided == 0) LMuted(t("app.projects.slug.proposals_accepted_drafts.no_pending_proposals_changed", "No pending proposals changed."))
            if (receipt.skipped > 0) Span(Modifier.toAttrs { attr("data-proposals-receipt-skipped", "true") }) {
                Text(tPlural("app.projects.slug.proposals.count.proposals_skipped", receipt.skipped,
                    "{count} proposal was not changed by this decision.", "{count} proposals were not changed by this decision."))
            }
            if (receipt.truncated) Span(Modifier.toAttrs { attr("data-proposals-receipt-truncated", "true") }) {
                Text(t("app.projects.slug.proposals_accepted_drafts.this_receipt_does_not_list_every",
                    "This receipt does not list every decided proposal. Only identified accepted drafts have review links."))
            }
        }
        if (state.unavailable) {
            LAlert(
                title = if (state.failure != null) t("app.projects.slug.proposals_accepted_drafts.that_didnt_work", "That didn't work")
                    else t("app.projects.slug.proposals_accepted_drafts.decision_details_unavailable", "Decision details unavailable"),
                description = state.failure ?: t("app.projects.slug.proposals_accepted_drafts.the_decision_may_have_completed_but",
                    "The decision may have completed, but its item details could not be confirmed. No additional accepted drafts are identified."),
                modifier = Modifier.attr("data-proposals-receipt-unavailable", "true"),
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        if (state.readRequired) {
            LMuted(t("app.projects.slug.proposals_accepted_drafts.reload_the_proposals_before_making_another", "Reload the proposals before making another decision."))
            LButton(t("app.projects.slug.proposals_accepted_drafts.reload_proposals", "Reload proposals"), onReload,
                enabled = !state.busy, variant = LButtonVariant.SECONDARY)
        }
    }
}

private external fun encodeURIComponent(value: String): String
